import { useSnapshot } from 'valtio';

import Text from '@cartwhl/ui/base/Text';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import WarningIcon from '@cartwhl/ui/icons/WarningIcon';
import Button from '@cartwhl/ui/input/Button';
import SubPanel from '@cartwhl/ui/layout/SubPanel';
import UploadError from '@cartwhl/ui/molecules/UploadError';
import {
  TRACKED_FEATURES,
  trackFeatureStepCompleted,
} from '@cartwhl/tracking/featureTracking';

import { characterPickerStore, uiStore } from 'stores';

import {
  UPLOAD_CHARACTER_HISTORY_TAB,
  UPLOAD_CHARACTER_TAB,
} from '../constants';
import { latest, uploadedChars } from '../utils';
import CharacterUploader from './CharacterUploader';
import UploadSuccess from './UploadSuccess';

interface UploadCharacterSubpanelProps {
  id: string;
  elementToTrack: HTMLButtonElement | HTMLDivElement | null;
}

export default function UploadCharacterSubpanel({
  id,
  elementToTrack,
}: UploadCharacterSubpanelProps) {
  const { characters, upload } = useSnapshot(characterPickerStore.state);
  const { subPanelSectionKey, subPanelKey } = useSnapshot(uiStore.state);

  return (
    <SubPanel
      id={id}
      elementToTrack={elementToTrack}
      closePanel={() => uiStore.setSubPanelKey('')}
      subPanelKey={subPanelKey}
    >
      <div className="mt-1 mb-4 flex items-center justify-between">
        <Text variant="studio/title/md">Upload your character</Text>
      </div>
      <div className="relative z-0">
        {subPanelSectionKey === UPLOAD_CHARACTER_TAB && (
          <>
            <CharacterUploader />

            <div className="flex justify-between px-2 pt-3">
              <a
                href="https://docs.getcartwheel.com/docs/custom-characters/upload#upload-guidelines"
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  trackFeatureStepCompleted(
                    TRACKED_FEATURES.CHARACTER_UPLOAD,
                    'upload_guidelines_clicked',
                    { metadata: { location: 'upload_subpanel' } },
                  )
                }
              >
                <Text variant="studio/body/medium/sm" className="text-blue">
                  Upload Guidelines
                </Text>
              </a>
              <button
                onClick={() =>
                  uiStore.setSubPanelSectionKey(UPLOAD_CHARACTER_HISTORY_TAB)
                }
              >
                <Text variant="studio/body/medium/sm" className="text-blue">
                  Upload activity
                </Text>
              </button>
            </div>
          </>
        )}
        {subPanelSectionKey === UPLOAD_CHARACTER_HISTORY_TAB && (
          <div>
            <Button
              variant="tertiary"
              icon={<ChevronLeftIcon />}
              onClick={() =>
                uiStore.setSubPanelSectionKey(UPLOAD_CHARACTER_TAB)
              }
            >
              Upload Activity
            </Button>
            <div className="mt-4 flex flex-col gap-4">
              {characters
                ?.filter(uploadedChars)
                .sort(latest)
                .map((c, i) => {
                  if (c.uploadStatus === 'FAILED') {
                    return (
                      <UploadError
                        key={c.jobID}
                        isLatestError={i === 0}
                        onTryAgainClick={
                          upload
                            ? () => {
                                characterPickerStore.removeUpload();
                                uiStore.setSubPanelSectionKey(
                                  UPLOAD_CHARACTER_TAB,
                                );
                              }
                            : undefined
                        }
                        errorReasons={c.failedReasons}
                        createdAt={c.createdAt}
                        primaryIcon={<WarningIcon />}
                        title="Upload failed to process"
                      />
                    );
                  }
                  if (c.uploadStatus === 'COMPLETE') {
                    return (
                      <UploadSuccess
                        key={c.jobID}
                        characterName={c.characterName}
                        createdAt={c.createdAt}
                        title="Character Upload"
                      />
                    );
                  }
                })}
              {characters?.filter(uploadedChars).length === 0 && (
                <div className="flex flex-col gap-4">
                  <Text variant="studio/body/md">No uploaded characters</Text>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </SubPanel>
  );
}
